import { ITextAttributes } from '../types';
import { TextAttributes, TextField, TextInline, TextList } from './types';
import { DEFAULT_STYLE_VALUES, StyleAttributes, CDIFtoQuillAttributeMapping } from './StyleAttributes';

interface TextOp {
    content: string;
    attributes: ITextAttributes;
}

function fontStyleToAttributes(fontStyle: string) {
    const styles = fontStyle.split(',');
    const stylesObj = styles.reduce((acc: ITextAttributes, style) => {
        const actualStyle = CDIFtoQuillAttributeMapping[style.toLowerCase()];
        if (StyleAttributes[actualStyle]) {
            acc[actualStyle] = StyleAttributes[actualStyle];
        }
        return acc;
    }, {});

    return { ...DEFAULT_STYLE_VALUES, ...stylesObj };
}
/**
 * It is important that we do not return an undefined or null value for any of the attributes from this function. That is
 * why they are each wrapped in a conditional check.
 *
 * If we do, when we attempt to format the Quill editor with an undefined or null value it will use a different overloaded
 * signature which assigns the attribute name as the 'source' which then causes it to emit change events when it shouldn't.
 */
function buildQuillAttributes(textField: Partial<TextAttributes>) {
    let result = {} as ITextAttributes;

    if (textField.fontStyle) {
        result = fontStyleToAttributes(textField.fontStyle);
    }

    if (textField.color) {
        result.color = textField.color;
    }

    if (textField.fontFamily) {
        result.fontFamily = textField.fontFamily;
    }

    if (textField.fontSize) {
        result.fontSize = parseFloat(textField.fontSize);
    }

    if (textField.casing) {
        result.casing = textField.casing;
    }

    if (textField.lineHeight) {
        result.lineHeight = textField.lineHeight;
    }

    if (textField.letterspacing) {
        result.letterspacing = textField.letterspacing;
    }

    if (textField.stroke) {
        if (textField.stroke.color) {
            result.strokeColor = textField.stroke.color;
        }

        if (textField.stroke.thickness) {
            result.strokeThickness = textField.stroke.thickness;
        }

        if (textField.stroke.overprints) {
            result.strokeOverprints = textField.stroke.overprints;
        }

        if (textField.stroke.position) {
            result.strokePosition = textField.stroke.position;
        }
    }

    if (textField.overprints) {
        result.overprints = textField.overprints;
    }

    return result;
}

function convertTextField(textField: TextField): TextOp {
    return {
        attributes: buildQuillAttributes(textField),
        content: textField.content,
    };
}

function convertTextInline(textInline: TextInline, inheritedAttributes: ITextAttributes): TextOp {
    const attributes = { ...inheritedAttributes, ...buildQuillAttributes(textInline) };
    return {
        attributes,
        content: textInline.content,
    };
}

function convertTextList(textList: TextList): TextOp[] {
    const listAttributes = buildQuillAttributes(textList);
    const ops: TextOp[] = [];

    for (const listItem of textList.content) {
        const listItemAttributes = { ...listAttributes, ...buildQuillAttributes(listItem) };
        for (const textInline of listItem.content) {
            ops.push(convertTextInline(textInline, listItemAttributes));
        }
        const listStyle = listItem.listMarkerStyle === 'unordered' ? 'bullet' : 'ordered';
        ops.push({
            attributes: {
                blockColor: listItemAttributes.color,
                blockFontSize: listItemAttributes.fontSize,
                blockFontFamily: listItemAttributes.fontFamily,
                blockCasing: listItemAttributes.casing,
                blockLineHeight: listItemAttributes.lineHeight,
                blockLetterspacing: listItemAttributes.letterspacing,
                blockFontWeight: listItemAttributes.fontWeight,
                blockFontStyle: listItemAttributes.fontStyle,
                blockTextDecoration: listItemAttributes.textDecoration,
                blockStrokeColor: listItemAttributes.strokeColor,
                blockStrokeOverprints: listItemAttributes.strokeOverprints,
                blockStrokeThickness: listItemAttributes.strokeThickness,
                blockStrokePosition: listItemAttributes.strokePosition,
                blockOverprints: listItemAttributes.overprints,
                list: listStyle,
                indent: listItem.depth ? listItem.depth - 1 : 0,
            },
            content: '\n',
        });
    }

    return ops;
}

function isTextList(textItem: TextField | TextList): textItem is TextList {
    return (textItem as TextList).type === 'list';
}

const concat = (acc: any[], curr: any[]) => [...acc, ...curr];

/**
 * We need to split newlines into their own op due to the way Quill handles newlines, esp. lines with only a newline
 */
function splitNewLines(field: TextOp) {
    if (field.attributes && field.attributes.list) {
        return [field];
    }
    const lines: string[] = field.content.split('\n');
    const lastLine = lines.length - 1;

    return lines.map((line, index) => {
        const content: TextOp[] = [{ ...field, content: line }];
        if (index < lastLine) {
            content.push({ ...field, content: '\n' });
        }
        return content;
    }).reduce(concat, []);
}

export function convertFieldsToDelta(textItems: (TextField | TextList)[]) {
    const ops: TextOp[] = [];
    for (const textItem of textItems) {
        const lastOp = ops[ops.length - 1];
        if (isTextList(textItem)) {
            // In CimDoc, there is an implicit newline before lists. So, we need to add that to the last op here.
            if (lastOp && !lastOp.attributes.list) {
                lastOp.content += '\n';
            }
            ops.push(...convertTextList(textItem));
        } else {
            ops.push(convertTextField(textItem));
        }
    }
    return ops.map(splitNewLines).reduce(concat, []);
}
